// Resumen de «Arma la venta» con los montos del servidor: panel lateral y barra inferior en tablet.
import type { ReactNode } from 'react';
import { formatMXN } from '@/shared/lib/format/money';
import {
    SALE_PROMOTION_FALLBACK,
    SalePromotionPrice,
    SalePromotionTags,
} from '@/shared/patterns/SalePromotion';
import type { SaleSummary } from '../buildSale';
import { promotionRefsOf, type LinePromotionRef } from '../cartLines';
import { FinancingBadge } from './FinancingBadge';
import type { LineSlots } from './LineLabels';

const mxn = (cents: number): string => formatMXN(cents / 100);

function CreditCap({ credit }: { credit: NonNullable<SaleSummary['credit']> }) {
    const pct =
        credit.capCents > 0
            ? Math.min(100, Math.round((credit.financedCents / credit.capCents) * 100))
            : 0;
    return (
        <div className="flex flex-col gap-1 text-[12px]">
            <div className="flex items-baseline justify-between gap-2 whitespace-nowrap">
                <span className="min-w-0 truncate">Extras a crédito con SureKeep</span>
                <span className="shrink-0 tabular-nums">
                    {mxn(credit.financedCents)} / {mxn(credit.capCents)}
                </span>
            </div>
            <div aria-hidden className="h-1.5 overflow-hidden rounded-full bg-muted">
                <span className="block h-full bg-primary" style={{ width: `${String(pct)}%` }} />
            </div>
        </div>
    );
}

/** Una etiqueta por promo: el nombre lo da Promociones (o «Promoción» sin pieza). */
function promotionNames(
    refs: readonly LinePromotionRef[],
    promotionLabel: LineSlots['promotionLabel'],
) {
    return refs.map((ref) =>
        promotionLabel ? (
            <span key={ref.promotionId}>
                {promotionLabel({
                    ...ref,
                    format: (n) => n,
                    fallback: SALE_PROMOTION_FALLBACK,
                    withAudience: true,
                })}
            </span>
        ) : (
            SALE_PROMOTION_FALLBACK
        ),
    );
}

/** Agrega la etiqueta propia del código después de las de las promos. */
function withCodeTag(names: ReactNode[], code: string | undefined): ReactNode[] {
    return code ? [...names, `Código ${code}`] : names;
}

/** Etiquetas de las promos del equipo: una por promo y otra para el código. */
function EquipmentPromotionTag({
    equipment,
    discounted,
    promotionLabel,
}: {
    equipment: NonNullable<SaleSummary['equipment']>;
    discounted: boolean;
    promotionLabel: LineSlots['promotionLabel'];
}) {
    const refs = discounted ? promotionRefsOf(equipment.line) : [];
    if (refs.length === 0 && !equipment.discountCode) return null;
    return (
        <span className="block" data-testid="equipment-discount">
            <SalePromotionTags
                names={withCodeTag(promotionNames(refs, promotionLabel), equipment.discountCode)}
            />
        </span>
    );
}

/** Etiquetas de las promos (y el código) que bajaron un extra. */
function ExtraPromotionTag({
    extra,
    promotionLabel,
}: {
    extra: SaleSummary['extras'][number];
    promotionLabel: LineSlots['promotionLabel'];
}) {
    const refs =
        extra.promotions ??
        (extra.promotionId === undefined
            ? []
            : [
                  {
                      promotionId: extra.promotionId,
                      ...(extra.promotionVersion !== undefined && {
                          promotionVersion: extra.promotionVersion,
                      }),
                  },
              ]);
    if (refs.length === 0 && !extra.discountCode) return null;
    return (
        <span className="block" data-testid="extra-discount">
            <SalePromotionTags
                names={withCodeTag(promotionNames(refs, promotionLabel), extra.discountCode)}
            />
        </span>
    );
}

/** Panel lateral; debajo de lg va al final de la columna y la barra lleva «Continuar». */
export function BuildSaleSummaryPanel({
    summary,
    continueButton,
    continueReason,
    showFinancing = true,
    afterSale,
    giftName,
    promotionLabel,
    cashTotalCents,
}: {
    summary: SaleSummary;
    continueButton: ReactNode;
    /** Motivo por el que «Continuar» no avanza; va justo debajo del botón. */
    continueReason?: ReactNode;
    /** Total sin las promos solo SureKeep; se muestra debajo solo si difiere. */
    cashTotalCents?: number | undefined;
    /** `false` sin nivel SureKeep: los extras no llevan la marca de financiable. */
    showFinancing?: boolean;
    /** «Después en la venta»: pieza de Promociones; trae su encabezado y no pinta nada si está vacía. */
    afterSale?: ReactNode;
    /** Nombre del regalo según Promociones; sin pieza queda el del catálogo. */
    giftName?: (gift: SaleSummary['gifts'][number]) => ReactNode;
    /** Nombre de la promo del equipo según Promociones; sin pieza, «Promoción». */
    promotionLabel?: LineSlots['promotionLabel'];
}) {
    const { equipment, gifts, extras, totalCents, credit } = summary;
    const discount = equipment ? equipment.baseCents - equipment.netCents : 0;
    return (
        <aside
            aria-label="Resumen de la venta"
            className="flex flex-col gap-2.5 rounded-lg border border-border bg-card p-4 text-[13px] lg:sticky lg:top-3"
        >
            <h2 className="text-[14px] font-semibold">Resumen</h2>
            {equipment && (
                <div className="flex justify-between gap-3">
                    <span className="min-w-0">
                        {equipment.name}
                        <EquipmentPromotionTag
                            equipment={equipment}
                            discounted={discount > 0}
                            promotionLabel={promotionLabel}
                        />
                    </span>
                    {discount > 0 ? (
                        <SalePromotionPrice
                            originalCents={equipment.baseCents}
                            netCents={equipment.netCents}
                            className="shrink-0"
                        />
                    ) : (
                        <span className="tabular-nums">{mxn(equipment.netCents)}</span>
                    )}
                </div>
            )}
            {gifts.map((g) => (
                <div key={g.key} className="flex justify-between gap-3">
                    <span className="min-w-0">{giftName ? giftName(g) : g.name}</span>
                    <span className="flex gap-1.5 tabular-nums">
                        <s className="text-muted-foreground">{mxn(g.valueCents)}</s>
                        <span className="text-info">{mxn(0)}</span>
                    </span>
                </div>
            ))}
            {extras.map((e) => {
                const discounted = e.baseCents !== undefined && e.baseCents > e.totalCents;
                return (
                    <div key={e.sku} className="flex items-start justify-between gap-3">
                        <span className="flex min-w-0 flex-col">
                            <span className="flex min-w-0 items-center gap-1.5">
                                <span className="min-w-0 truncate" title={e.name}>
                                    {e.name}
                                    {e.qty > 1 && ` × ${String(e.qty)}`}
                                </span>
                                {showFinancing && (
                                    <FinancingBadge
                                        financeable={e.financeable}
                                        className="flex-none"
                                    />
                                )}
                            </span>
                            {discounted && (
                                <ExtraPromotionTag extra={e} promotionLabel={promotionLabel} />
                            )}
                        </span>
                        {discounted && e.baseCents !== undefined ? (
                            <SalePromotionPrice
                                originalCents={e.baseCents}
                                netCents={e.totalCents}
                                className="shrink-0"
                            />
                        ) : (
                            <span className="tabular-nums">{mxn(e.totalCents)}</span>
                        )}
                    </div>
                );
            })}
            <div className="border-t border-border" />
            <div className="flex items-baseline justify-between gap-3">
                <span className="font-semibold">Total</span>
                <span className="flex flex-col items-end">
                    <b className="text-[18px] tabular-nums">{mxn(totalCents)}</b>
                    {cashTotalCents !== undefined && cashTotalCents !== totalCents && (
                        <span
                            data-testid="cash-total"
                            className="text-[11.5px] text-muted-foreground tabular-nums"
                        >
                            De contado {mxn(cashTotalCents)}
                        </span>
                    )}
                </span>
            </div>
            {credit && <CreditCap credit={credit} />}
            <div className="hidden flex-col gap-1.5 lg:flex">
                {continueButton}
                {continueReason}
            </div>
            {afterSale}
        </aside>
    );
}

/** Barra inferior fija en tablet: total, tope y «Continuar». */
export function BuildSaleSummaryBar({
    summary,
    continueButton,
    continueReason,
    cashTotalCents,
}: {
    summary: SaleSummary;
    continueButton: ReactNode;
    /** Motivo por el que «Continuar» no avanza; va justo debajo del botón. */
    continueReason?: ReactNode;
    /** Total sin las promos solo SureKeep; se muestra debajo solo si difiere. */
    cashTotalCents?: number | undefined;
}) {
    return (
        <div className="fixed inset-x-0 bottom-0 z-10 flex items-center gap-3 border-t border-border bg-card px-4 py-2.5 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] lg:hidden">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex justify-between gap-2 text-[12px]">
                    <span>Total</span>
                    <span className="flex flex-col items-end">
                        <b className="text-[16px] tabular-nums">{mxn(summary.totalCents)}</b>
                        {cashTotalCents !== undefined && cashTotalCents !== summary.totalCents && (
                            <span
                                data-testid="cash-total-bar"
                                className="text-[11px] text-muted-foreground tabular-nums"
                            >
                                De contado {mxn(cashTotalCents)}
                            </span>
                        )}
                    </span>
                </div>
                {summary.credit && <CreditCap credit={summary.credit} />}
            </div>
            <div className="flex max-w-[55%] flex-none flex-col items-end gap-1 text-right">
                {continueButton}
                {continueReason}
            </div>
        </div>
    );
}
